/**
 * Payout Account Setup — /payout-account
 * KYC + bank details for anyone receiving marketplace payments
 * (Trade Unions, Studios, Vendors, Producers, Ad Agencies).
 *
 * Mirrors the union-claims UX so union owners feel at home.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShieldCheck, Upload, ArrowLeft, Loader2, FileCheck2, Sparkles,
  Landmark, IdCard, FileText, Files, BadgeCheck, Clock3, AlertTriangle,
  XCircle,
} from "lucide-react";

const DOC_META = {
  pan_card:                 { icon: IdCard,   label: "PAN Card" },
  registration_certificate: { icon: FileText, label: "Registration Certificate" },
  cancelled_cheque:         { icon: FileCheck2, label: "Cancelled Cheque" },
  board_resolution:         { icon: Files,    label: "Board Resolution / Authorization Letter" },
};

const ENTITY_LABEL = {
  union:    "Trade Union",
  studio:   "Studio",
  vendor:   "Vendor",
  producer: "Production House",
  agency:   "Agency",
};

const STATUS_META = {
  pending:        { color: "#F5B82E", label: "Pending review",  Icon: Clock3 },
  info_requested: { color: "#F59E0B", label: "Info requested",  Icon: AlertTriangle },
  verified:       { color: "#22C55E", label: "Verified",        Icon: BadgeCheck },
  rejected:       { color: "#EF4444", label: "Rejected",        Icon: XCircle },
};

export default function PayoutAccountSetup() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [meta, setMeta]         = useState(null);
  const [existing, setExisting] = useState(null);
  const [loading, setL]         = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    legal_name:     "",
    pan:            "",
    account_holder: "",
    bank_name:      "",
    account_number: "",
    ifsc:           "",
  });
  const [files, setFiles] = useState({
    pan_card: null, registration_certificate: null,
    cancelled_cheque: null, board_resolution: null,
  });

  useEffect(() => {
    (async () => {
      try {
        const [mRes, listRes] = await Promise.all([
          api.get(`/payout-accounts/meta`),
          api.get(`/payout-accounts/mine`).catch(() => ({ data: { payout_accounts: [] } })),
        ]);
        setMeta(mRes.data);
        const active = (listRes.data.payout_accounts || []).find(
          (r) => ["pending", "info_requested", "verified"].includes(r.kyc_status),
        );
        // Also surface most recent rejected row so the user can resubmit.
        const rejected = (listRes.data.payout_accounts || []).find(
          (r) => r.kyc_status === "rejected",
        );
        setExisting(active || rejected || null);
        if (user) {
          setForm(f => ({
            ...f,
            legal_name:     f.legal_name     || user.name || "",
            account_holder: f.account_holder || user.name || "",
          }));
        }
      } finally { setL(false); }
    })();
    /* eslint-disable-next-line */
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center text-white/40 uppercase tracking-[0.3em] text-xs">Loading…</div>;
  }
  if (!user) return null;

  const eligible = !!(meta && meta.entity_types.length);
  const canSubmit = !existing || existing.kyc_status === "rejected";
  const canResubmit = existing?.kyc_status === "info_requested";
  const statusVal = existing ? STATUS_META[existing.kyc_status] : null;

  const setFile = (k, f) => setFiles(prev => ({ ...prev, [k]: f }));

  async function submit(e) {
    e.preventDefault();
    for (const k of Object.keys(files)) {
      if (!files[k]) {
        toast.error(`Upload ${DOC_META[k].label} to continue`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, (v || "").trim()));
      Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));
      await api.post(`/payout-accounts`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      toast.success("Payout account submitted — verification in 24-48h");
      // Refresh
      const listRes = await api.get(`/payout-accounts/mine`);
      const active = (listRes.data.payout_accounts || []).find(
        (r) => ["pending", "info_requested", "verified"].includes(r.kyc_status),
      );
      setExisting(active || null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submit failed");
    } finally { setSubmitting(false); }
  }

  async function submitResubmit(e) {
    e.preventDefault();
    if (!existing) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));
      await api.post(`/payout-accounts/${existing.id}/resubmit`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      toast.success("Docs resubmitted — verification will re-open");
      const listRes = await api.get(`/payout-accounts/mine`);
      const active = (listRes.data.payout_accounts || []).find(
        (r) => ["pending", "info_requested", "verified"].includes(r.kyc_status),
      );
      setExisting(active || null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submit failed");
    } finally { setSubmitting(false); }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="payout-account-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => nav(-1)}
          className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
          data-testid="payout-back">
          <ArrowLeft size={12} /> Back
        </button>

        <div className="mt-3 text-[10px] tracking-[0.32em] uppercase text-emerald-300">
          Marketplace · Payout Setup
        </div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight"
          data-testid="payout-title">
          Payout Account & KYC
        </h1>
        <div className="mt-2 text-sm text-white/60 max-w-xl">
          Complete this once — every marketplace payment (union membership fees, vendor
          invoices, agency fees) that lands in your favour will settle to the bank
          account on file. Verification takes 24-48 hours.
        </div>

        {/* Status */}
        {existing && statusVal && (
          <div className="mt-6 rounded-2xl border p-5"
            style={{ borderColor: `${statusVal.color}55`, background: `${statusVal.color}10` }}
            data-testid="payout-status-card">
            <div className="flex items-start gap-3">
              <statusVal.Icon size={18} style={{ color: statusVal.color }} />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.28em]"
                  style={{ color: statusVal.color }}>
                  {statusVal.label}
                </div>
                <div className="mt-1 text-sm">
                  <span className="text-white/75">{existing.entity_name}</span>
                  <span className="mx-2 text-white/30">·</span>
                  <span className="uppercase tracking-[0.22em] text-[10px] text-white/50">
                    {ENTITY_LABEL[existing.entity_type] || existing.entity_type}
                  </span>
                </div>
                <div className="mt-2 text-xs text-white/60 space-x-4">
                  <span>Bank: <b className="text-white/80">{existing.bank_name}</b></span>
                  <span>A/C: <b className="text-white/80">{existing.account_masked}</b></span>
                  <span>IFSC: <b className="text-white/80">{existing.ifsc}</b></span>
                </div>
                {existing.admin_note && (
                  <div className="mt-3 border-l-2 pl-3 text-white/70 text-xs"
                    style={{ borderColor: statusVal.color }}>
                    <div className="text-[9px] uppercase tracking-[0.22em] mb-1"
                      style={{ color: statusVal.color }}>
                      Onboarding note
                    </div>
                    {existing.admin_note}
                  </div>
                )}
                {existing.rejection_reason && (
                  <div className="mt-3 border-l-2 pl-3 text-xs text-red-200/90"
                    style={{ borderColor: "#EF4444" }}>
                    <div className="text-[9px] uppercase tracking-[0.22em] mb-1 text-red-300/80">
                      Rejection reason
                    </div>
                    {existing.rejection_reason}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Resubmit flow */}
        {canResubmit && (
          <form onSubmit={submitResubmit} className="mt-6 space-y-6" data-testid="payout-resubmit-form">
            <div className="text-[11px] text-white/55">
              Upload only the docs the onboarding team flagged — bank details stay the same.
            </div>
            <DocGrid files={files} setFile={setFile} maxBytes={meta?.max_doc_bytes || 0} optional />
            <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 flex items-center gap-1.5">
                <ShieldCheck size={11} /> Docs stay encrypted at rest
              </div>
              <Button type="submit" disabled={submitting}
                className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-6 h-11"
                data-testid="payout-resubmit">
                {submitting
                  ? (<><Loader2 size={12} className="animate-spin mr-2" /> Submitting…</>)
                  : (<>Resubmit docs <Sparkles size={12} className="ml-2" /></>)}
              </Button>
            </div>
          </form>
        )}

        {/* Not eligible */}
        {!eligible && !existing && (
          <div className="mt-6 rounded-2xl border border-white/10 p-6 text-sm text-white/60"
            data-testid="payout-not-eligible">
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-2">
              Not eligible yet
            </div>
            Only trade-union owners (post-claim approval), studio admins, vendors,
            production houses, and agency admins can open a payout account.
            <Link to="/" className="ml-2 text-emerald-300 hover:text-emerald-200 underline">
              Back home →
            </Link>
          </div>
        )}

        {/* Fresh submission form */}
        {canSubmit && eligible && (
          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="payout-form">

            {/* Legal + PAN */}
            <fieldset className="rounded-2xl border border-white/10 p-6">
              <legend className="px-2 text-[10px] uppercase tracking-[0.28em] text-white/50">
                Legal entity
              </legend>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Legal name *">
                  <Input required value={form.legal_name}
                    data-testid="payout-legal-name"
                    onChange={e => setForm({...form, legal_name: e.target.value})}
                    placeholder="As on registration certificate"
                    className="bg-black/40 border-white/15" />
                </Field>
                <Field label="PAN (10 chars) *">
                  <Input required maxLength={10} minLength={10}
                    value={form.pan}
                    data-testid="payout-pan"
                    onChange={e => setForm({...form, pan: e.target.value.toUpperCase()})}
                    placeholder="AAAAA9999A"
                    className="bg-black/40 border-white/15 uppercase tracking-wider" />
                </Field>
              </div>
            </fieldset>

            {/* Bank */}
            <fieldset className="rounded-2xl border border-white/10 p-6">
              <legend className="px-2 text-[10px] uppercase tracking-[0.28em] text-white/50">
                Bank account
              </legend>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Account holder *">
                  <Input required value={form.account_holder}
                    data-testid="payout-holder"
                    onChange={e => setForm({...form, account_holder: e.target.value})}
                    className="bg-black/40 border-white/15" />
                </Field>
                <Field label="Bank name *">
                  <Input required value={form.bank_name}
                    data-testid="payout-bank-name"
                    onChange={e => setForm({...form, bank_name: e.target.value})}
                    placeholder="e.g. HDFC Bank"
                    className="bg-black/40 border-white/15" />
                </Field>
                <Field label="Account number *">
                  <Input required value={form.account_number}
                    data-testid="payout-acc-number"
                    onChange={e => setForm({...form, account_number: e.target.value.replace(/\s/g, "")})}
                    className="bg-black/40 border-white/15 tracking-wider" />
                </Field>
                <Field label="IFSC (11 chars) *">
                  <Input required maxLength={11} minLength={11}
                    value={form.ifsc}
                    data-testid="payout-ifsc"
                    onChange={e => setForm({...form, ifsc: e.target.value.toUpperCase().replace(/\s/g, "")})}
                    placeholder="HDFC0001234"
                    className="bg-black/40 border-white/15 uppercase tracking-wider" />
                </Field>
              </div>
              <div className="mt-3 text-[11px] text-white/45 flex items-center gap-1.5">
                <Landmark size={11} /> Salaries route to this account only after
                onboarding verifies the cancelled cheque.
              </div>
            </fieldset>

            {/* Docs */}
            <fieldset className="rounded-2xl border border-white/10 p-6">
              <legend className="px-2 text-[10px] uppercase tracking-[0.28em] text-white/50">
                KYC documents *
              </legend>
              <div className="text-[11px] text-white/50 mb-4">
                PDF · JPG · PNG · WebP — max{" "}
                {Math.round((meta?.max_doc_bytes || 0) / (1024*1024))} MB each. All four are required.
              </div>
              <DocGrid files={files} setFile={setFile} maxBytes={meta?.max_doc_bytes || 0} />
            </fieldset>

            <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 flex items-center gap-1.5">
                <ShieldCheck size={11} /> Docs stay encrypted at rest
              </div>
              <Button type="submit" disabled={submitting}
                className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-6 h-11"
                data-testid="payout-submit">
                {submitting
                  ? (<><Loader2 size={12} className="animate-spin mr-2" /> Submitting…</>)
                  : (<>Submit for verification <Sparkles size={12} className="ml-2" /></>)}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function DocGrid({ files, setFile, maxBytes, optional }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {Object.entries(DOC_META).map(([key, { icon: Icon, label }]) => (
        <DocSlot key={key}
          slotKey={key}
          label={label}
          Icon={Icon}
          file={files[key]}
          onFile={(f) => setFile(key, f)}
          maxBytes={maxBytes}
          optional={optional} />
      ))}
    </div>
  );
}

function DocSlot({ slotKey, label, Icon, file, onFile, maxBytes, optional }) {
  const ref = useRef(null);
  return (
    <div>
      <input ref={ref} type="file" className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
        onChange={(e) => {
          const f = e.target.files?.[0] || null;
          if (f && maxBytes && f.size > maxBytes) {
            toast.error(`${label} exceeds ${Math.round(maxBytes/(1024*1024))} MB`);
            return;
          }
          onFile(f);
        }}
        data-testid={`payout-file-${slotKey}`} />
      <button type="button" onClick={() => ref.current?.click()}
        className="w-full rounded-xl border-2 border-dashed border-white/15 hover:border-emerald-400/50 hover:bg-emerald-400/5 transition p-5 text-left flex items-center gap-3"
        data-testid={`payout-drop-${slotKey}`}>
        <Icon size={20} className={file ? "text-emerald-400" : "text-white/40"} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">
            {label} {optional && "(if flagged)"}
          </div>
          {file ? (
            <>
              <div className="text-sm font-medium truncate">{file.name}</div>
              <div className="text-[10px] text-white/45">
                {(file.size / 1024).toFixed(1)} KB · click to replace
              </div>
            </>
          ) : (
            <div className="text-[12px] text-white/50 inline-flex items-center gap-1.5 mt-0.5">
              <Upload size={11} /> Choose file
            </div>
          )}
        </div>
      </button>
    </div>
  );
}
